import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import PublicLayout from '../../layouts/PublicLayout';
import AuthStep from '../../components/public/AuthStep';
import CancellationModal from '../../components/public/CancellationModal';
import { usePublicAuth } from '../../context/PublicAuthContext';
import { User } from 'lucide-react';

const MainMenu = () => {
    const [searchParams] = useSearchParams();
    const phone = searchParams.get('phone');
    const navigate = useNavigate();
    const { isAuthenticated, client } = usePublicAuth();

    const [appointments, setAppointments] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [cancellingId, setCancellingId] = useState<number | null>(null);
    const [viewingAppt, setViewingAppt] = useState<any | null>(null);

    // Auth Modal State
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [linkSent, setLinkSent] = useState(false);
    const [sendingLink, setSendingLink] = useState(false);

    // Fetch appointments
    useEffect(() => {
        if (!phone) return;

        const fetchAppointments = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_BASE_URL}/api/public/appointments/?phone=${phone}`);
                setAppointments(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchAppointments();
    }, [phone]);

    const handleProfileClick = () => {
        if (isAuthenticated) {
            navigate('/public/profile');
        } else {
            setShowAuthModal(true);
        }
    };

    const handleRequestLink = async () => {
        setSendingLink(true);
        try {
            await axios.post(`${API_BASE_URL}/api/public/auth/request-link/`, { phone });
            setLinkSent(true);
        } catch (err) {
            alert('Erro ao enviar link. Verifique se seu telefone está correto.');
            console.error(err);
        } finally {
            setSendingLink(false);
        }
    };

    const getFullPhotoUrl = (path: string | null | undefined) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${API_BASE_URL}${path}`;
    };

    // If no phone, show AuthStep
    if (!phone) {
        return (
            <PublicLayout>
                <AuthStep />
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            {/* Header with Profile Info */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3">
                <div>
                    <h2 className="m-0">Meus Agendamentos</h2>
                    {client && isAuthenticated && (
                        <div className="d-flex align-items-center gap-2 mt-2">
                            {client.photo ? (
                                <img src={getFullPhotoUrl(client.photo)} className="rounded-circle" style={{ width: 30, height: 30, objectFit: 'cover' }} alt="Profile" />
                            ) : (
                                <span className="badge bg-secondary rounded-circle p-2">{client.name?.charAt(0)}</span>
                            )}
                            <small className="text-muted">Olá, {client.name}</small>
                        </div>
                    )}
                </div>

                <div className="d-flex gap-2">
                    <button onClick={handleProfileClick} className="btn btn-outline-primary d-flex align-items-center gap-2">
                        <User size={18} /> Meu Perfil
                    </button>
                    <Link to={`/public/schedule?phone=${phone}`} className="btn btn-success d-flex align-items-center gap-2">
                        + Novo Agendamento
                    </Link>
                </div>
            </div>

            {loading && <p>Carregando...</p>}

            {!loading && appointments.length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', background: 'white', borderRadius: '8px' }}>
                    <p>Nenhum agendamento encontrado.</p>
                </div>
            )}

            <div className="list-container bg-transparent shadow-none p-0">
                <div className="row">
                    {appointments.map(appt => (
                        <div key={appt.id} className="col-12 mb-3">
                            <div
                                className="card h-100 shadow-sm glass-effect"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setViewingAppt(appt)}
                                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                onMouseOut={(e) => e.currentTarget.style.transform = 'none'}
                            >
                                <div className="card-body p-0">
                                    <div className="row g-0 align-items-center">
                                        {/* Photo Section */}
                                        <div className="col-auto p-3">
                                            {appt.professional_photo ? (
                                                <img
                                                    src={getFullPhotoUrl(appt.professional_photo)}
                                                    alt={appt.professional_name}
                                                    className="rounded-circle"
                                                    style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded-circle bg-light d-flex align-items-center justify-content-center text-secondary"
                                                    style={{ width: '60px', height: '60px', fontSize: '1.5rem' }}
                                                >
                                                    {appt.professional_name ? appt.professional_name.charAt(0) : '?'}
                                                </div>
                                            )}
                                        </div>

                                        {/* Info Section */}
                                        <div className="col p-3">
                                            <div className="row g-3">
                                                <div className="col-md-4 col-12">
                                                    <span className="small text-muted text-uppercase d-block mb-1">Profissional</span>
                                                    <strong className="d-block text-dark">{appt.professional_name}</strong>
                                                </div>
                                                <div className="col-md-4 col-12">
                                                    <span className="small text-muted text-uppercase d-block mb-1">Serviço</span>
                                                    <strong className="d-block text-dark">{appt.service_name}</strong>
                                                </div>
                                                <div className="col-md-4 col-12">
                                                    <span className="small text-muted text-uppercase d-block mb-1">Data e Hora</span>
                                                    <strong className="d-block text-dark">
                                                        {new Date(appt.start_time).toLocaleDateString()} às {new Date(appt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </strong>
                                                </div>
                                                <div className="col-12 mt-2 pt-2 border-top border-light">
                                                    <span className="small text-muted text-uppercase d-block mb-1">Para</span>
                                                    <strong className="text-dark d-block fs-5">
                                                        {appt.customer_name || appt.client_name || 'Cliente'}
                                                    </strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status & Action Footer */}
                                    <div className="card-footer bg-light p-3 border-top-0 d-flex justify-content-between align-items-center">
                                        <span
                                            className={`badge rounded-pill ${appt.status === 'CANCELLED' ? 'bg-danger-subtle text-danger' : appt.status === 'PENDING' ? 'bg-warning-subtle text-warning-emphasis' : 'bg-success-subtle text-success'} px-3 py-2`}
                                        >
                                            {appt.status === 'PENDING' ? 'Agendado' : appt.status === 'CONFIRMED' ? 'Confirmado' : appt.status === 'CANCELLED' ? 'Cancelado' : appt.status}
                                        </span>

                                        {appt.status !== 'CANCELLED' && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCancellingId(appt.id);
                                                }}
                                                className="btn btn-outline-danger btn-sm fw-bold text-nowrap"
                                            >
                                                CANCELAR
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {cancellingId && (
                <CancellationModal
                    appointmentId={cancellingId}
                    onClose={() => setCancellingId(null)}
                    onSuccess={() => {
                        setCancellingId(null);
                        window.location.reload();
                    }}
                />
            )}

            {/* Login / Magic Link Modal */}
            {showAuthModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Acesso Seguro ao Perfil</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAuthModal(false)}></button>
                            </div>
                            <div className="modal-body text-center">
                                {linkSent ? (
                                    <div className="py-4">
                                        <div className="display-1 mb-3">📩</div>
                                        <h5>Link enviado!</h5>
                                        <p className="text-muted">Verifique seu WhatsApp ({phone}). Enviamos um link de acesso seguro para você editar seus dados.</p>
                                        <button className="btn btn-primary mt-3" onClick={() => setShowAuthModal(false)}>Entendi</button>
                                    </div>
                                ) : (
                                    <div className="py-3">
                                        <p>Para proteger seus dados, precisamos confirmar sua identidade.</p>
                                        <p>Enviaremos um <strong>link de acesso seguro</strong> para o seu WhatsApp cadastrado:</p>
                                        <h4 className="my-3 text-primary">{phone}</h4>
                                        <button
                                            className="btn btn-success w-100 py-2 fs-5"
                                            onClick={handleRequestLink}
                                            disabled={sendingLink}
                                        >
                                            {sendingLink ? 'Enviando...' : 'Enviar Link no WhatsApp'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Details Modal */}
            {viewingAppt && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', width: '90%', maxWidth: '500px', position: 'relative' }}>
                        <button onClick={() => setViewingAppt(null)} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>&times;</button>

                        <h2 style={{ marginTop: 0, marginBottom: '1.5rem', borderBottom: '1px solid #eee', paddingBottom: '1rem' }}>Detalhes do Agendamento</h2>

                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center' }}>
                            {viewingAppt.professional_photo ? (
                                <img src={getFullPhotoUrl(viewingAppt.professional_photo)} alt={viewingAppt.professional_name} style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }} />
                            ) : (
                                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: '#888' }}>
                                    {viewingAppt.professional_name.charAt(0)}
                                </div>
                            )}
                            <div>
                                <h3 style={{ margin: 0 }}>{viewingAppt.professional_name}</h3>
                                <p style={{ margin: 0, color: '#666' }}>Profissional</p>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <div>
                                <strong style={{ display: 'block', color: '#666', fontSize: '0.9rem' }}>Serviço</strong>
                                <span style={{ fontSize: '1.1rem' }}>{viewingAppt.service_name} ({viewingAppt.service_duration})</span>
                            </div>
                            <div>
                                <strong style={{ display: 'block', color: '#666', fontSize: '0.9rem' }}>Data e Hora</strong>
                                <span style={{ fontSize: '1.1rem' }}>
                                    {new Date(viewingAppt.start_time).toLocaleDateString()} às {new Date(viewingAppt.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <div>
                                <strong style={{ display: 'block', color: '#666', fontSize: '0.9rem' }}>Para</strong>
                                <span style={{ fontSize: '1.1rem' }}>{viewingAppt.customer_name || viewingAppt.client_name || 'Cliente'}</span>
                            </div>
                            <div>
                                <strong style={{ display: 'block', color: '#666', fontSize: '0.9rem' }}>Situação</strong>
                                <span style={{
                                    color: viewingAppt.status === 'CANCELLED' ? '#dc3545' : viewingAppt.status === 'PENDING' ? '#ffc107' : '#198754',
                                    fontWeight: 500,
                                    textShadow: viewingAppt.status === 'PENDING' ? '0px 0px 1px #997404' : 'none'
                                }}>
                                    {viewingAppt.status === 'PENDING' ? 'Agendado' : viewingAppt.status === 'CONFIRMED' ? 'Confirmado' : viewingAppt.status === 'CANCELLED' ? 'Cancelado' : viewingAppt.status}
                                </span>
                            </div>
                        </div>

                        <button
                            onClick={() => setViewingAppt(null)}
                            style={{
                                marginTop: '2rem',
                                width: '100%',
                                padding: '0.75rem',
                                background: '#f8f9fa',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            )}

        </PublicLayout>
    );
};

export default MainMenu;
